import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Loader2, Info } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomizeGiftCard = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  
  const [template, setTemplate] = useState(null);
  const [plans, setPlans] = useState([]);
  const [messageOptions, setMessageOptions] = useState([]);
  const [redeemInstructions, setRedeemInstructions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  
  const [formData, setFormData] = useState({
    customMessage: '',
    usePrewrittenMessage: false,
    selectedPrewrittenMessage: '',
    subscriptionPlan: '',
    subscriptionPlanId: '',  // NEW: Store plan ID
    recipientName: '',
    recipientEmail: '',
    buyerName: '',
    buyerEmail: '',
    deliveryDate: new Date().toISOString().split('T')[0],
    deliveryTime: 'immediate',
    scheduledTime: '12:00',
    timezone: 'America/New_York',
    customOccasionTitle: '',
    customWishText: ''
  });

  // Detect language from URL
  const getLangFromURL = () => {
    const path = location.pathname;
    if (path.includes('-ru')) return 'ru';
    if (path.includes('-kz')) return 'kz';
    return 'en';
  };

  useEffect(() => {
    const urlLang = getLangFromURL();
    if (i18n.language !== urlLang) {
      i18n.changeLanguage(urlLang);
    }
    fetchData(urlLang);
  }, [templateId, location.pathname]);

  const fetchData = async (lang = 'en') => {
    try {
      const [templatesRes, plansRes, messagesRes, instructionsRes] = await Promise.all([
        axios.get(`${API}/templates?lang=${lang}`),
        axios.get(`${API}/subscription-plans?lang=${lang}`),
        axios.get(`${API}/messages?lang=${lang}`),
        axios.get(`${API}/site-content/redemption-instructions?lang=${lang}`)
      ]);
      
      const selectedTemplate = templatesRes.data.find(t => t.id === templateId);
      setTemplate(selectedTemplate);
      setPlans(plansRes.data);
      setMessageOptions(messagesRes.data);
      setRedeemInstructions(instructionsRes.data.instructions || []);
      
      if (plansRes.data.length > 0) {
        // Pre-select the default plan (is_default: true from database)
        const defaultPlan = plansRes.data.find(p => p.is_default);
        if (defaultPlan) {
          setFormData(prev => ({ 
            ...prev, 
            subscriptionPlan: defaultPlan.plan_title,
            subscriptionPlanId: defaultPlan.id  // Store plan ID
          }));
        } else {
          // Fallback to first plan if no default set
          setFormData(prev => ({ 
            ...prev, 
            subscriptionPlan: plansRes.data[0].plan_title,
            subscriptionPlanId: plansRes.data[0].id  // Store plan ID
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const finalMessage = formData.usePrewrittenMessage 
      ? formData.selectedPrewrittenMessage 
      : formData.customMessage;
    
    if (!finalMessage.trim()) {
      alert(t('customize.messageRequired'));
      return;
    }
    
    const selectedPlan = plans.find(p => p.plan_title === formData.subscriptionPlan);
    
    // Calculate delivery datetime with timezone
    let deliveryDatetime;
    if (formData.deliveryTime === 'immediate') {
      deliveryDatetime = new Date().toISOString();
    } else {
      const dateTimeString = `${formData.deliveryDate}T${formData.scheduledTime}:00`;
      deliveryDatetime = new Date(dateTimeString).toISOString();
    }
    
    const urlLang = getLangFromURL();
    const checkoutData = {
      templateId: template.id,
      template: template,
      subscriptionPlan: formData.subscriptionPlan,
      subscriptionPlanId: formData.subscriptionPlanId,  // NEW: Send plan ID
      subscriptionPlanDuration: selectedPlan?.duration_text || '3 months',
      customMessage: finalMessage,
      recipientName: formData.recipientName,
      recipientEmail: formData.recipientEmail,
      buyerName: formData.buyerName,
      buyerEmail: formData.buyerEmail,
      deliveryDatetime: deliveryDatetime,
      deliveryTimezone: formData.timezone,
      amount: selectedPlan?.price || 0,
      language: urlLang  // Add language to checkout data
    };
    
    const suffix = urlLang === 'en' ? '' : `-${urlLang}`;
    navigate(`/checkout${suffix}`, { state: checkoutData });
  };

  if (loading || !template) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-12 h-12 text-[#1E88E5] animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 md:mb-8 gap-4">
          <h1 className="text-3xl md:text-4xl font-bold text-black">{t('customize.pageTitle')}</h1>
          <button
            onClick={() => setShowHowItWorks(!showHowItWorks)}
            className="flex items-center gap-2 px-4 py-3 bg-blue-50 text-[#1E88E5] rounded-lg hover:bg-blue-100 transition-colors min-h-[48px]"
          >
            <Info className="w-5 h-5" />
            {t('customize.howItWorksButton')}
          </button>
        </div>

        {/* How It Works Modal/Section */}
        {showHowItWorks && (
          <div className="mb-6 md:mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-[#1E88E5] rounded-xl p-4 md:p-6 shadow-lg">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl md:text-2xl font-bold text-black flex items-center gap-2">
                🎁 How Your Gift Card Works
              </h3>
              <button
                onClick={() => setShowHowItWorks(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none min-h-[44px] min-w-[44px] flex items-center justify-center"
              >
                ×
              </button>
            </div>
            <div className="space-y-4 text-gray-700">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="font-semibold text-[#1E88E5] mb-2">📧 Step 1: Recipient Gets Email</p>
                <p>Your recipient will receive a beautifully designed email with their personalized gift card and unique redemption code.</p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="font-semibold text-[#1E88E5] mb-2">📝 Step 2: Clear Instructions Included</p>
                <p>The email contains step-by-step instructions on how to redeem their MethodMate subscription at <strong>methodmate.ai/selectgiftplan</strong></p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="font-semibold text-[#1E88E5] mb-2">✨ Step 3: Free Subscription Activated</p>
                <p>They enter the code, verify the final amount is $0, and complete registration to start their subscription!</p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4 border-l-4 border-yellow-400">
                <p className="text-sm"><strong>Note:</strong> Each code can only be used once and is valid for the specific plan shown on the gift card.</p>
              </div>
            </div>
          </div>
        )}
        
        <div className="grid lg:grid-cols-2 gap-8 md:gap-12">
          {/* Email Preview */}
          <div>
            <h2 className="text-xl md:text-2xl font-semibold mb-3 md:mb-4 text-black">{t('customize.emailPreview')}</h2>
            <p className="text-sm text-gray-600 mb-3 md:mb-4">{t('customize.previewDescription')}</p>
            
            <div className="lg:sticky lg:top-24">
              <div className="bg-white rounded-lg shadow-2xl overflow-hidden border border-gray-200" style={{ maxWidth: '600px' }}>
                {/* Header */}
                <div className="bg-[#1E88E5] p-8 text-center">
                  <div className="text-4xl font-bold text-white tracking-wider">METHODMATE</div>
                </div>
                
                {/* Content */}
                <div className="p-10">
                  <p className="text-2xl mb-2">{t('customize.greeting').replace('[Recipient]', formData.recipientName || t('customize.greeting').match(/\[([^\]]+)\]/)[1])}</p>
                  <p className="text-gray-600 mb-8">{t('customize.senderMessage').replace('[Your Name]', formData.buyerName || t('customize.senderMessage').match(/\[([^\]]+)\]/)[1])}</p>
                  
                  {/* Occasion Title */}
                  <h1 
                    className="font-bold text-center mb-4 text-gray-800"
                    style={{ 
                      fontSize: `${template.occasion_title_font_size || 36}px`,
                      lineHeight: '1.2'
                    }}
                  >
                    {template.is_custom && formData.customOccasionTitle 
                      ? formData.customOccasionTitle 
                      : template.occasion_title}
                  </h1>
                  
                  {/* Wish Text */}
                  <h4 className="text-lg italic text-center text-gray-600 mb-8">
                    {template.is_custom && formData.customWishText 
                      ? formData.customWishText 
                      : template.wish_text}
                  </h4>
                  
                  {/* Custom Message */}
                  {(formData.customMessage || formData.selectedPrewrittenMessage) && (
                    <div className="bg-amber-50 p-5 rounded-lg mb-8 text-center">
                      <p className="text-xl italic text-gray-700">
                        "{formData.usePrewrittenMessage ? formData.selectedPrewrittenMessage : formData.customMessage}"
                      </p>
                    </div>
                  )}
                  
                  {/* Tagline */}
                  <h3 className="text-2xl text-center text-gray-700 mb-8">
                    {t('customize.boostProductivity')}
                  </h3>
                  
                  {/* Coupon Box */}
                  <div className="bg-blue-50 border-2 border-dashed border-[#1E88E5] p-8 rounded-lg text-center mb-6">
                    <div className="text-lg font-bold text-gray-800 mb-3">{t('customize.yourGiftCode')}</div>
                    <div className="text-4xl font-bold text-[#1E88E5] tracking-wider mb-3">XXXX-XXXX-XXXX</div>
                    <div className="text-gray-600">
                      {t('customize.validFor')} <strong>{
                        (() => {
                          const selectedPlan = plans.find(p => p.plan_title === formData.subscriptionPlan);
                          if (!selectedPlan) return formData.subscriptionPlan || '[Select a Plan]';
                          
                          // Translate plan name based on language
                          const priceRounded = Math.round(selectedPlan.price);
                          if (i18n.language === 'ru') {
                            if (priceRounded === 29) return "Индивидуальный квартальный план";
                            if (priceRounded === 99) return "Индивидуальный годовой план";
                          } else if (i18n.language === 'kz') {
                            if (priceRounded === 29) return "Жеке кварталдық жоспар үшін";
                            if (priceRounded === 99) return "Жеке жылдық жоспар үшін";
                          }
                          return formData.subscriptionPlan;
                        })()
                      }</strong>
                    </div>
                  </div>
                  
                  {/* Instructions Preview */}
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-bold mb-3">{t('customize.howToRedeem')}</h3>
                    <ol className="text-sm text-gray-700 space-y-2 pl-5 list-decimal">
                      {redeemInstructions.length > 0 ? (
                        redeemInstructions.map((instruction, index) => (
                          <li key={index}>{instruction}</li>
                        ))
                      ) : (
                        <>
                          <li>{t('customize.redeemStep1')}</li>
                          <li>{t('customize.redeemStep2')}</li>
                          <li>{t('customize.redeemStep3')}</li>
                          <li>{t('customize.redeemStep4')}</li>
                          <li>{t('customize.redeemStep5')}</li>
                          <li>{t('customize.redeemStep6')}</li>
                          <li>{t('customize.redeemStep7')}</li>
                        </>
                      )}
                    </ol>
                    <div className="mt-4 p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm">
                      {t('customize.codeOnceOnly')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Custom Template Fields */}
              {template.is_custom && (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 space-y-4">
                  <h3 className="text-lg font-semibold text-blue-900">✨ {t('customize.designYourOwnCard')}</h3>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('customize.occasionTitle')} *
                    </label>
                    <input
                      type="text"
                      value={formData.customOccasionTitle}
                      onChange={(e) => setFormData({ ...formData, customOccasionTitle: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder={t('customize.occasionPlaceholder')}
                      maxLength={50}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Wish Text *
                    </label>
                    <textarea
                      value={formData.customWishText}
                      onChange={(e) => setFormData({ ...formData, customWishText: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Wishing you all the best on your special day!"
                      rows="3"
                      maxLength={150}
                    />
                  </div>
                </div>
              )}

              {/* Subscription Plan Selection */}
              <div>
                <label className="block text-sm font-semibold text-black mb-3">
                  {t('customize.selectPlan')} {t('customize.planRequired')}
                </label>
                <div className="grid md:grid-cols-2 gap-4 mb-2">
                  {plans.map(plan => {
                    // Plan data comes pre-localized from API based on language parameter
                    const planTitle = plan.plan_title;
                    const planDesc = plan.description || '';
                    
                    return (
                      <button
                        key={plan.id}
                        type="button"
                        onClick={() => setFormData({ 
                          ...formData, 
                          subscriptionPlan: plan.plan_title,
                          subscriptionPlanId: plan.id  // Store plan ID
                        })}
                        className={`p-4 border-2 rounded-lg text-left transition-all ${
                          formData.subscriptionPlan === plan.plan_title
                            ? 'border-[#1E88E5] bg-[#E3F2FD]'
                            : 'border-gray-300 hover:border-[#1E88E5]'
                        }`}
                        data-testid={`plan-box-${plan.plan_name}`}
                      >
                        <div className="font-semibold text-black">{planTitle}</div>
                        <div className="text-2xl font-bold text-[#1E88E5] mt-1">${plan.price.toFixed(2)}</div>
                        <div className="text-sm text-gray-600 mt-1">{planDesc}</div>
                      </button>
                    );
                  })}
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  {t('customize.giftRecipient')}
                </p>
              </div>

              {/* Message Type Toggle */}
              <div>
                <label className="block text-sm font-semibold text-black mb-2">
                  {t('customize.message')} {t('customize.messageRequired')}
                </label>
                <div className="flex space-x-4 mb-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, usePrewrittenMessage: false })}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      !formData.usePrewrittenMessage
                        ? 'bg-[#1E88E5] text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {t('customize.customMessage')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, usePrewrittenMessage: true })}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      formData.usePrewrittenMessage
                        ? 'bg-[#1E88E5] text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {t('customize.selectFromTemplates')}
                  </button>
                </div>

                {formData.usePrewrittenMessage ? (
                  <select
                    value={formData.selectedPrewrittenMessage}
                    onChange={(e) => setFormData({ ...formData, selectedPrewrittenMessage: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                    required
                    data-testid="prewritten-message-select"
                  >
                    <option value="">{t('customize.customMessagePlaceholder').replace('Write your personal message here', 'Select a message')}...</option>
                    {messageOptions.map(msg => (
                      <option key={msg.id} value={msg.message_text}>
                        {msg.message_text}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div>
                    <textarea
                      value={formData.customMessage}
                      onChange={(e) => setFormData({ ...formData, customMessage: e.target.value })}
                      placeholder={t('customize.customMessagePlaceholder')}
                      rows={4}
                      maxLength={80}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                      required={!formData.usePrewrittenMessage}
                      data-testid="custom-message-input"
                    />
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-xs text-gray-500">{t('customize.maxCharacters')}</span>
                      <span className={`text-xs font-semibold ${
                        formData.customMessage.length > 70 
                          ? 'text-orange-600' 
                          : formData.customMessage.length === 80 
                          ? 'text-red-600' 
                          : 'text-gray-500'
                      }`}>
                        {formData.customMessage.length}/80
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Recipient Details */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-black mb-2">
                    {t('customize.recipientName')} {t('customize.recipientNameRequired')}
                  </label>
                  <input
                    type="text"
                    value={formData.recipientName}
                    onChange={(e) => setFormData({ ...formData, recipientName: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                    required
                    data-testid="recipient-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-black mb-2">
                    {t('customize.recipientEmail')} {t('customize.recipientEmailRequired')}
                  </label>
                  <input
                    type="email"
                    value={formData.recipientEmail}
                    onChange={(e) => setFormData({ ...formData, recipientEmail: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                    required
                    data-testid="recipient-email-input"
                  />
                </div>
              </div>

              {/* Buyer Details */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-black mb-2">
                    {t('customize.yourName')} {t('customize.yourNameRequired')}
                  </label>
                  <input
                    type="text"
                    value={formData.buyerName}
                    onChange={(e) => setFormData({ ...formData, buyerName: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                    required
                    data-testid="buyer-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-black mb-2">
                    {t('customize.yourEmail')} {t('customize.yourEmailRequired')}
                  </label>
                  <input
                    type="email"
                    value={formData.buyerEmail}
                    onChange={(e) => setFormData({ ...formData, buyerEmail: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                    required
                    data-testid="buyer-email-input"
                  />
                </div>
              </div>

              {/* Delivery Time */}
              <div>
                <label className="block text-sm font-semibold text-black mb-2">
                  {t('customize.deliverySchedule')} *
                </label>
                <select
                  value={formData.deliveryTime}
                  onChange={(e) => setFormData({ ...formData, deliveryTime: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent mb-3"
                  data-testid="delivery-time-select"
                >
                  <option value="immediate">{t('customize.sendImmediately')}</option>
                  <option value="scheduled">{t('customize.scheduleForLater')}</option>
                </select>

                {formData.deliveryTime === 'scheduled' && (
                  <div className="space-y-3">
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                        <input
                          type="date"
                          value={formData.deliveryDate}
                          onChange={(e) => setFormData({ ...formData, deliveryDate: e.target.value })}
                          min={new Date().toISOString().split('T')[0]}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                          required
                          data-testid="delivery-date-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                        <input
                          type="time"
                          value={formData.scheduledTime}
                          onChange={(e) => setFormData({ ...formData, scheduledTime: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                          required
                          data-testid="delivery-time-input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                      <select
                        value={formData.timezone}
                        onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                        data-testid="timezone-select"
                      >
                        <option value="Etc/GMT+12">(UTC-12:00) International Date Line West</option>
                        <option value="Pacific/Midway">(UTC-11:00) Coordinated Universal Time-11</option>
                        <option value="Pacific/Honolulu">(UTC-10:00) Hawaii</option>
                        <option value="America/Anchorage">(UTC-09:00) Alaska</option>
                        <option value="America/Los_Angeles">(UTC-08:00) Pacific Time (US & Canada)</option>
                        <option value="America/Denver">(UTC-07:00) Mountain Time (US & Canada)</option>
                        <option value="America/Whitehorse">(UTC-07:00) Yukon</option>
                        <option value="America/Chicago">(UTC-06:00) Central Time (US & Canada)</option>
                        <option value="America/Mexico_City">(UTC-06:00) Guadalajara, Mexico City, Monterrey</option>
                        <option value="America/Bogota">(UTC-05:00) Bogota, Lima, Quito, Rio Branco</option>
                        <option value="America/New_York">(UTC-05:00) Eastern Time (US & Canada)</option>
                        <option value="America/Havana">(UTC-05:00) Havana</option>
                        <option value="America/Indiana/Indianapolis">(UTC-05:00) Indiana (East)</option>
                        <option value="America/Grand_Turk">(UTC-05:00) Turks and Caicos</option>
                        <option value="America/Asuncion">(UTC-04:00) Asuncion</option>
                        <option value="America/Halifax">(UTC-04:00) Atlantic Time (Canada)</option>
                        <option value="America/Santiago">(UTC-04:00) Santiago</option>
                        <option value="America/Araguaina">(UTC-03:00) Araguaina</option>
                        <option value="America/Sao_Paulo">(UTC-03:00) Brasilia</option>
                        <option value="America/Argentina/Buenos_Aires">(UTC-03:00) City of Buenos Aires</option>
                        <option value="America/Montevideo">(UTC-03:00) Montevideo</option>
                        <option value="Atlantic/South_Georgia">(UTC-02:00) Mid-Atlantic</option>
                        <option value="Atlantic/Azores">(UTC-01:00) Azores</option>
                        <option value="Europe/London">(UTC+00:00) Dublin, Edinburgh, Lisbon, London</option>
                        <option value="Africa/Monrovia">(UTC+00:00) Monrovia, Reykjavik</option>
                        <option value="Africa/Sao_Tome">(UTC+00:00) Sao Tome</option>
                        <option value="Africa/Casablanca">(UTC+01:00) Casablanca</option>
                        <option value="Europe/Berlin">(UTC+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna</option>
                        <option value="Europe/Belgrade">(UTC+01:00) Belgrade, Bratislava, Budapest, Ljubljana, Prague</option>
                        <option value="Europe/Paris">(UTC+01:00) Brussels, Copenhagen, Madrid, Paris</option>
                        <option value="Europe/Warsaw">(UTC+01:00) Sarajevo, Skopje, Warsaw, Zagreb</option>
                        <option value="Africa/Lagos">(UTC+01:00) West Central Africa</option>
                        <option value="Europe/Athens">(UTC+02:00) Athens, Bucharest</option>
                        <option value="Asia/Beirut">(UTC+02:00) Beirut</option>
                        <option value="Africa/Cairo">(UTC+02:00) Cairo</option>
                        <option value="Asia/Gaza">(UTC+02:00) Gaza, Hebron</option>
                        <option value="Africa/Johannesburg">(UTC+02:00) Harare, Pretoria</option>
                        <option value="Europe/Kiev">(UTC+02:00) Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius</option>
                        <option value="Asia/Jerusalem">(UTC+02:00) Jerusalem</option>
                        <option value="Africa/Juba">(UTC+02:00) Juba</option>
                        <option value="Europe/Kaliningrad">(UTC+02:00) Kaliningrad</option>
                        <option value="Africa/Tripoli">(UTC+02:00) Tripoli</option>
                        <option value="Europe/Istanbul">(UTC+03:00) Istanbul</option>
                        <option value="Asia/Riyadh">(UTC+03:00) Kuwait, Riyadh</option>
                        <option value="Europe/Moscow">(UTC+03:00) Moscow, St. Petersburg</option>
                        <option value="Asia/Dubai">(UTC+04:00) Abu Dhabi, Muscat</option>
                        <option value="Asia/Baku">(UTC+04:00) Baku</option>
                        <option value="Europe/Saratov">(UTC+04:00) Saratov</option>
                        <option value="Asia/Tbilisi">(UTC+04:00) Tbilisi</option>
                        <option value="Asia/Yerevan">(UTC+04:00) Yerevan</option>
                        <option value="Asia/Kabul">(UTC+04:30) Kabul</option>
                        <option value="Asia/Tashkent">(UTC+05:00) Astana, Tashkent</option>
                        <option value="Asia/Yekaterinburg">(UTC+05:00) Ekaterinburg</option>
                        <option value="Asia/Karachi">(UTC+05:00) Islamabad, Karachi</option>
                        <option value="Asia/Kolkata">(UTC+05:30) Chennai, Kolkata, Mumbai, New Delhi</option>
                        <option value="Asia/Colombo">(UTC+05:30) Sri Jayawardenepura</option>
                        <option value="Asia/Kathmandu">(UTC+05:45) Kathmandu</option>
                        <option value="Asia/Almaty">(UTC+06:00) Astana</option>
                        <option value="Asia/Dhaka">(UTC+06:00) Dhaka</option>
                        <option value="Asia/Omsk">(UTC+06:00) Omsk</option>
                        <option value="Asia/Yangon">(UTC+06:30) Yangon (Rangoon)</option>
                        <option value="Asia/Bangkok">(UTC+07:00) Bangkok, Hanoi, Jakarta</option>
                        <option value="Asia/Hovd">(UTC+07:00) Hovd</option>
                        <option value="Asia/Novosibirsk">(UTC+07:00) Novosibirsk</option>
                        <option value="Asia/Shanghai">(UTC+08:00) Beijing, Chongqing, Hong Kong, Urumqi</option>
                        <option value="Asia/Singapore">(UTC+08:00) Kuala Lumpur, Singapore</option>
                        <option value="Asia/Taipei">(UTC+08:00) Taipei</option>
                        <option value="Asia/Tokyo">(UTC+09:00) Osaka, Sapporo, Tokyo</option>
                        <option value="Asia/Seoul">(UTC+09:00) Seoul</option>
                        <option value="Australia/Brisbane">(UTC+10:00) Brisbane</option>
                        <option value="Australia/Sydney">(UTC+10:00) Canberra, Melbourne, Sydney</option>
                        <option value="Asia/Magadan">(UTC+11:00) Magadan</option>
                        <option value="Pacific/Norfolk">(UTC+11:00) Norfolk Island</option>
                        <option value="Asia/Sakhalin">(UTC+11:00) Sakhalin</option>
                        <option value="Pacific/Guadalcanal">(UTC+11:00) Solomon Is., New Caledonia</option>
                        <option value="Asia/Kamchatka">(UTC+12:00) Anadyr, Petropavlovsk-Kamchatsky</option>
                        <option value="Pacific/Auckland">(UTC+12:00) Auckland, Wellington</option>
                        <option value="Pacific/Fiji">(UTC+12:00) Fiji</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <button
                type="submit"
                className="w-full px-6 py-4 bg-[#1E88E5] text-white text-lg font-semibold rounded-lg hover:bg-[#1565C0] transition-all transform hover:scale-105 shadow-lg"
                data-testid="proceed-to-checkout-btn"
              >
                Proceed to Checkout
              </button>
            </form>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CustomizeGiftCard;